import React from 'react'
import { Box, Typography, Button, CircularProgress, Tooltip } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import GridOnIcon from '@mui/icons-material/GridOn'
import CropFreeIcon from '@mui/icons-material/CropFree'
import { downloadBlob } from '../utils/downloadCsv'
import { api } from '../api'
import { TRAWL_STATION_COLUMNS, formatTrawlRowForExport } from './PointDataTableTrawl'
import { LONGLINE_STATION_COLUMNS, formatLonglineRowForExport } from './PointDataTableLongline'
import { TRAWL_BIOLOGY_COLUMNS } from './PointDataTableTrawlBiology'
import { LONGLINE_BIOLOGY_COLUMNS, formatLonglineBiologyRowForExport } from './PointDataTableLonglineBiology'
import { PLANKTON_STATION_COLUMNS, formatPlanktonStationRowForExport } from './PointDataTablePlankton'
import { PLANKTON_BIOLOGY_COLUMNS, formatPlanktonBiologyRowForExport } from './PointDataTablePlanktonBiology'

const COMPACT_RESULTS_BAR_QUERY = '@media (max-height: 760px), (max-width: 1180px)'
const COMPACT_BUTTON_SX = {
  [COMPACT_RESULTS_BAR_QUERY]: {
    minHeight: 26,
    px: 0.75,
    fontSize: '0.68rem',
    '& .MuiButton-startIcon': {
      mr: 0.35,
    },
  },
}

/**
 * Slim "Results" strip that surfaces the CSV export the moment a query
 * returns, without requiring the user to click a point first. Renders only
 * when `rows.length > 0`. Picks the column set + row formatter for the
 * active tab so its CSV matches the per-point detail export byte-for-byte.
 */
const TAB_EXPORT_CONFIG = {
  TrawlStations: {
    label: 'stations',
    columns: TRAWL_STATION_COLUMNS,
    format: formatTrawlRowForExport,
    filename: 'seamap-trawl-stations.csv',
  },
  LonglineStations: {
    label: 'stations',
    columns: LONGLINE_STATION_COLUMNS,
    format: formatLonglineRowForExport,
    filename: 'seamap-longline-stations.csv',
  },
  TrawlBiology: {
    label: 'catches',
    columns: TRAWL_BIOLOGY_COLUMNS,
    format: (row) => ({ ...row }),
    filename: 'seamap-trawl-biology.csv',
  },
  LonglineBiology: {
    label: 'catches',
    columns: LONGLINE_BIOLOGY_COLUMNS,
    format: formatLonglineBiologyRowForExport,
    filename: 'seamap-longline-biology.csv',
  },
  PlanktonStations: {
    label: 'stations',
    columns: PLANKTON_STATION_COLUMNS,
    format: formatPlanktonStationRowForExport,
    filename: 'seamap-plankton-stations.csv',
  },
  PlanktonBiology: {
    label: 'catches',
    columns: PLANKTON_BIOLOGY_COLUMNS,
    format: formatPlanktonBiologyRowForExport,
    filename: 'seamap-plankton-biology.csv',
  },
}

const STATION_EXPORT_COLUMNS = [
  { label: 'Survey', key: 'survey' },
  { label: 'Station ID', key: 'stationid' },
  { label: 'SEAMAP Station', key: 'seamap_station' },
  { label: 'SID', key: 'sid' },
  { label: 'Source', key: 'source' },
  { label: 'Date', key: 'date' },
  { label: 'Year', key: 'year' },
  { label: 'Latitude', key: 'latitude' },
  { label: 'Longitude', key: 'longitude' },
  { label: 'Cruise No.', key: 'cruise_no' },
  { label: 'Vessel', key: 'vessel' },
  { label: 'Cruise Title', key: 'cruise_title' },
  { label: 'Start Depth (m)', key: 'depth_start_m' },
  { label: 'End Depth (m)', key: 'depth_end_m' },
  { label: 'Depth (m)', key: 'depth_m' },
  { label: 'Gear', key: 'gear' },
]

const CATCH_EXPORT_COLUMNS = [
  { label: 'Survey', key: 'survey' },
  { label: 'Station ID', key: 'stationid' },
  { label: 'SEAMAP Station', key: 'seamap_station' },
  { label: 'SID', key: 'sid' },
  { label: 'Source', key: 'source' },
  { label: 'Date', key: 'date' },
  { label: 'Year', key: 'year' },
  { label: 'Latitude', key: 'latitude' },
  { label: 'Longitude', key: 'longitude' },
  { label: 'Cruise No.', key: 'cruise_no' },
  { label: 'Genus', key: 'genus' },
  { label: 'Species', key: 'species' },
  { label: 'Biocode', key: 'biocode' },
  { label: 'Matched Taxon', key: 'matched_taxon' },
  { label: 'Count', key: 'count' },
  { label: 'Total Weight', key: 'total_weight' },
  { label: 'BGSID', key: 'bgsid' },
  { label: 'Sex', key: 'sex' },
  { label: 'PCL (mm)', key: 'pcl' },
  { label: 'SL (mm)', key: 'sl' },
  { label: 'FL (mm)', key: 'fl' },
  { label: 'TL (mm)', key: 'tl' },
  { label: 'DW (mm)', key: 'dw' },
  { label: 'Weight', key: 'weight' },
  { label: 'Initial Condition', key: 'initial_condition' },
  { label: 'Release Condition', key: 'release_condition' },
  { label: 'Fish ID', key: 'fish_id' },
]

const getZipFilename = (activeTab, suffix = 'displayed-records') => {
  const survey = activeTab.includes('Plankton') ? 'plankton' : (activeTab.includes('Longline') ? 'longline' : 'trawl')
  return `seamap-${survey}-${suffix}.zip`
}

const getHeatmapMetricTooltip = (activeTab, metric) => {
  const isLongline = activeTab === 'LonglineBiology'
  const copy = {
    raw: 'Map total selected-species catch as # individuals per 10\' x 10\' cell.',
    cpue: isLongline
      ? 'Map selected-species relative abundance as #/100 hooks.'
      : 'Map selected-species relative abundance as #/hectare swept.',
    biomass_cpue: isLongline
      ? 'Map selected-species relative biomass as kg/100 hooks.'
      : 'Map selected-species relative biomass as kg/hectare swept.',
    effort: isLongline
      ? 'Map sampling effort as # hooks per 10\' x 10\' cell.'
      : 'Map sampling effort as hectares swept per 10\' x 10\' cell.',
    encounter_rate: 'Map encounter rate as % stations with positive captures.',
  }
  return copy[metric] || 'Map the selected heat map metric.'
}

const getSelectAreaTooltip = (displayMode) => (
  displayMode === 'heatmap'
    ? 'When viewing heat maps, select area allows users to select all stations within the selected 10\' x 10\' cells and export the station/catch records.'
    : 'Select area allows users to select any number of stations and export the station/catch records.'
)

const getQuerySpeciesCount = (queryContext) => (
  Array.isArray(queryContext?.filters?.species)
    ? queryContext.filters.species.filter(species => typeof species === 'string' && species.trim()).length
    : 0
)

export { STATION_EXPORT_COLUMNS, CATCH_EXPORT_COLUMNS, getZipFilename }

export default function ResultsExportBar({
  activeTab,
  rows,
  queryContext,
  displayMode,
  onDisplayModeChange,
  heatmapMetric,
  onHeatmapMetricChange,
  onStartAreaSelect,
  selectionEnabled,
}) {
  const [isExporting, setIsExporting] = React.useState(false)
  const [exportError, setExportError] = React.useState('')

  if (!Array.isArray(rows) || rows.length === 0) return null
  const config = TAB_EXPORT_CONFIG[activeTab]
  if (!config) return null
  const isBiologyTab = activeTab === 'TrawlBiology' || activeTab === 'LonglineBiology' || activeTab === 'PlanktonBiology'
  const supportsHeatmap = activeTab === 'TrawlBiology' || activeTab === 'LonglineBiology'
  const isHeatmapBlockedBySpecies = isBiologyTab && getQuerySpeciesCount(queryContext) > 1
  const heatmapButtonDisabled = !queryContext || isHeatmapBlockedBySpecies
  const heatmapButtonTooltip = isHeatmapBlockedBySpecies
    ? 'Please filter for only one species to view heat maps'
    : 'Show a biology heat map'

  const handleExport = async () => {
    if (!queryContext) return
    setIsExporting(true)
    setExportError('')
    try {
      const response = await api.post('/export-records/download', queryContext, { responseType: 'blob' })
      downloadBlob(response.data, getZipFilename(activeTab))
    } catch (error) {
      console.error('Export records failed:', error)
      setExportError('Export failed')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'white',
        padding: { xs: '6px 12px', sm: '8px 16px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        borderTop: '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb',
        flexWrap: 'wrap',
        [COMPACT_RESULTS_BAR_QUERY]: {
          padding: '4px 8px',
          gap: 0.5,
        },
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        flexWrap: 'wrap',
        [COMPACT_RESULTS_BAR_QUERY]: {
          gap: 0.75,
        },
      }}>
        <Typography
          sx={{
            fontFamily: "'Nunito', 'Roboto', sans-serif",
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            fontWeight: 600,
            color: '#2E5A88',
            [COMPACT_RESULTS_BAR_QUERY]: {
              fontSize: '0.75rem',
            },
          }}
        >
          {rows.length.toLocaleString()} {config.label} displayed
        </Typography>
        {exportError && (
          <Typography sx={{ color: '#b91c1c', fontSize: '0.75rem', fontWeight: 700 }}>
            {exportError}
          </Typography>
        )}
      </Box>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        [COMPACT_RESULTS_BAR_QUERY]: {
          gap: 0.35,
          flexWrap: 'nowrap',
          maxWidth: '100%',
          overflowX: 'auto',
          pb: 0.25,
          scrollbarWidth: 'thin',
        },
      }}>
        {supportsHeatmap && (
          <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #d8dee6', borderRadius: '4px', overflow: 'hidden' }}>
            <Tooltip title="Show the filtered station or biology points">
              <span>
                <Button
                  size="small"
                  onClick={() => onDisplayModeChange('markers')}
                  disabled={!queryContext}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={displayMode === 'markers' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: displayMode === 'markers' ? 'white' : '#2E5A88',
                    backgroundColor: displayMode === 'markers' ? '#2E5A88' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: displayMode === 'markers' ? '#2E5A88' : 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Points
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={heatmapButtonTooltip}>
              <span>
                <Button
                  size="small"
                  startIcon={<GridOnIcon />}
                  onClick={() => onDisplayModeChange('heatmap')}
                  disabled={heatmapButtonDisabled}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={displayMode === 'heatmap' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: displayMode === 'heatmap' ? 'white' : '#2E5A88',
                    backgroundColor: displayMode === 'heatmap' ? '#8b0000' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: displayMode === 'heatmap' ? '#8b0000' : 'transparent',
                      boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                      color: '#9ca3af',
                      backgroundColor: '#f3f4f6',
                    },
                  }}
                >
                  Heat map
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
        {supportsHeatmap && displayMode === 'heatmap' && (
          <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #d8dee6', borderRadius: '4px', overflow: 'hidden' }}>
            <Tooltip title={getHeatmapMetricTooltip(activeTab, 'raw')}>
              <span>
                <Button
                  size="small"
                  onClick={() => onHeatmapMetricChange('raw')}
                  disabled={!queryContext}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={heatmapMetric === 'raw' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: heatmapMetric === 'raw' ? 'white' : '#2E5A88',
                    backgroundColor: heatmapMetric === 'raw' ? '#2E5A88' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: heatmapMetric === 'raw' ? '#2E5A88' : 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Total Catch
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={getHeatmapMetricTooltip(activeTab, 'cpue')}>
              <span>
                <Button
                  size="small"
                  onClick={() => onHeatmapMetricChange('cpue')}
                  disabled={!queryContext}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={heatmapMetric === 'cpue' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: heatmapMetric === 'cpue' ? 'white' : '#2E5A88',
                    backgroundColor: heatmapMetric === 'cpue' ? '#8b0000' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: heatmapMetric === 'cpue' ? '#8b0000' : 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Relative Abundance
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={getHeatmapMetricTooltip(activeTab, 'biomass_cpue')}>
              <span>
                <Button
                  size="small"
                  onClick={() => onHeatmapMetricChange('biomass_cpue')}
                  disabled={!queryContext}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={heatmapMetric === 'biomass_cpue' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: heatmapMetric === 'biomass_cpue' ? 'white' : '#2E5A88',
                    backgroundColor: heatmapMetric === 'biomass_cpue' ? '#a8551c' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: heatmapMetric === 'biomass_cpue' ? '#a8551c' : 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Relative Biomass
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={getHeatmapMetricTooltip(activeTab, 'effort')}>
              <span>
                <Button
                  size="small"
                  onClick={() => onHeatmapMetricChange('effort')}
                  disabled={!queryContext}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={heatmapMetric === 'effort' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: heatmapMetric === 'effort' ? 'white' : '#2E5A88',
                    backgroundColor: heatmapMetric === 'effort' ? '#0f766e' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: heatmapMetric === 'effort' ? '#0f766e' : 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Effort
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={getHeatmapMetricTooltip(activeTab, 'encounter_rate')}>
              <span>
                <Button
                  size="small"
                  onClick={() => onHeatmapMetricChange('encounter_rate')}
                  disabled={!queryContext}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  variant={heatmapMetric === 'encounter_rate' ? 'contained' : 'text'}
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: heatmapMetric === 'encounter_rate' ? 'white' : '#2E5A88',
                    backgroundColor: heatmapMetric === 'encounter_rate' ? '#5b5f24' : 'transparent',
                    fontWeight: 600,
                    ...COMPACT_BUTTON_SX,
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: heatmapMetric === 'encounter_rate' ? '#5b5f24' : 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Encounter Rate
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
        <Tooltip title={getSelectAreaTooltip(displayMode)}>
          <span>
            <Button
              size="small"
              startIcon={<CropFreeIcon />}
              onClick={onStartAreaSelect}
              disabled={!queryContext}
              variant={selectionEnabled ? 'contained' : 'text'}
              sx={{
                fontSize: '0.75rem',
                textTransform: 'none',
                color: selectionEnabled ? 'white' : '#2E5A88',
                backgroundColor: selectionEnabled ? '#2E5A88' : 'transparent',
                fontWeight: 600,
                ...COMPACT_BUTTON_SX,
              }}
            >
              Select area
            </Button>
          </span>
        </Tooltip>
        <Button
          size="small"
          startIcon={isExporting ? <CircularProgress size={14} /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={!queryContext || isExporting}
          sx={{
            fontSize: '0.75rem',
            textTransform: 'none',
            color: '#2E5A88',
            fontWeight: 600,
            ...COMPACT_BUTTON_SX,
          }}
        >
          Export All Points (ZIP)
        </Button>
      </Box>
    </Box>
  )
}
